import { useQueryClient } from "@tanstack/react-query";

import type { CommonModels } from "@/data/common/common.models";
import { type InvalidateQueryOptions, QueryModule, invalidateQueries } from "@/data/invalidateQueries";
import { useAuthGuardedMutation } from "@/hooks/useAuthGuardedMutation";
import { useAuthGuardedQuery } from "@/hooks/useAuthGuardedQuery";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { BizProfileApi } from "./bizProfile.api";
import type { BizProfileModels } from "./bizProfile.models";

export namespace BizProfileQueries {
  export const moduleName = QueryModule.BizProfile;

  export const keys = {
    all: [moduleName] as const,
    list: (
      limit: number,
      order?: string,
      page?: number,
      cursor?: string,
      filterExperienceYearsMax?: number,
      filterExperienceYearsMin?: number,
      filterRating?: BizProfileModels.BizProfileListFilterRatingParam,
      filterMsaLocation?: string,
      filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam,
      filterJobTitle?: string,
      filterSearch?: string,
    ) =>
      [
        ...keys.all,
        "/biz/profile/list",
        limit,
        order,
        page,
        cursor,
        filterExperienceYearsMax,
        filterExperienceYearsMin,
        filterRating,
        filterMsaLocation,
        filterTagName,
        filterJobTitle,
        filterSearch,
      ] as const,
    listLocations: (limit: number, order?: string, page?: number, cursor?: string, filterLocation?: string) =>
      [...keys.all, "/biz/profile/locations/list", limit, order, page, cursor, filterLocation] as const,
    listJobTitles: (
      limit: number,
      order?: string,
      page?: number,
      cursor?: string,
      filterExperienceYears?: BizProfileModels.ExperienceYearsFilterDto,
      filterMsaLocation?: string,
      filterJobTitle?: string,
    ) =>
      [
        ...keys.all,
        "/biz/profile/job-titles/list",
        limit,
        order,
        page,
        cursor,
        filterExperienceYears,
        filterMsaLocation,
        filterJobTitle,
      ] as const,
    listJobPositions: (limit: number, order?: string, page?: number, cursor?: string, filterJobPosition?: string) =>
      [...keys.all, "/biz/profile/job-positions/list", limit, order, page, cursor, filterJobPosition] as const,
    get: (id: string) => [...keys.all, "/biz/profile/:id", id] as const,
    getJobSkillMatrix: (id: string) => [...keys.all, "/biz/profile/:id/job-skill-matrix", id] as const,
    downloadResume: (id: string) => [...keys.all, "/biz/profile/:id/download", id] as const,
  };

  /**
   * Mutation `useRequestUpload`
   * @param { CommonModels.MediaUploadFileRequest } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> }
   * @statusCodes [201, default]
   */
  export const useRequestUpload = (
    options?: AppMutationOptions<typeof BizProfileApi.requestUpload, { data: CommonModels.MediaUploadFileRequest }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizProfileApi.requestUpload(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useBulkCreate`
   * @param { BizProfileModels.BulkCreateBizProfileRequestDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.BulkCreateBizProfileResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useBulkCreate = (
    options?: AppMutationOptions<
      typeof BizProfileApi.bulkCreate,
      { data: BizProfileModels.BulkCreateBizProfileRequestDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizProfileApi.bulkCreate(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useList`
   * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
   * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, name, location, city, state, jobTitle, jobPosition, rating, rank, experience, experienceYears, ldNumber, isFavorited, createdAt, updatedAt, readBy. Example: `id`
   * @param { number } object.page Query parameter. 1-indexed page number to begin from
   * @param { string } object.cursor Query parameter. ID of item to start after
   * @param { number } object.filterExperienceYearsMax Query parameter. Filter by experience years (upper bound). Example: `5`
   * @param { number } object.filterExperienceYearsMin Query parameter. Filter by experience years (lower bound). Example: `1`
   * @param { BizProfileModels.BizProfileListFilterRatingParam } object.filterRating Query parameter. Filter by rating. Example: `1`
   * @param { string } object.filterMsaLocation Query parameter. Filter by MSA location name (exact match). Example: `Los Angeles-Long Beach-Anaheim, CA`
   * @param { BizProfileModels.BizProfileListFilterTagNameParam } object.filterTagName Query parameter. Filter by tag name (exact match). Example: `Professional`
   * @param { string } object.filterJobTitle Query parameter. Filter by singular job title (exact match). Example: `Software Developer`
   * @param { string } object.filterSearch Query parameter. Filter by name, job title, comment, degrees (contains)
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.BizProfileListResponse> }
   * @statusCodes [200]
   */
  export const useList = <TData>(
    {
      limit,
      order,
      page,
      cursor,
      filterExperienceYearsMax,
      filterExperienceYearsMin,
      filterRating,
      filterMsaLocation,
      filterTagName,
      filterJobTitle,
      filterSearch,
    }: {
      limit: number;
      order?: string;
      page?: number;
      cursor?: string;
      filterExperienceYearsMax?: number;
      filterExperienceYearsMin?: number;
      filterRating?: BizProfileModels.BizProfileListFilterRatingParam;
      filterMsaLocation?: string;
      filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam;
      filterJobTitle?: string;
      filterSearch?: string;
    },
    options?: AppQueryOptions<typeof BizProfileApi.list, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.list(
        limit,
        order,
        page,
        cursor,
        filterExperienceYearsMax,
        filterExperienceYearsMin,
        filterRating,
        filterMsaLocation,
        filterTagName,
        filterJobTitle,
        filterSearch,
      ),
      queryFn: () =>
        BizProfileApi.list(
          limit,
          order,
          page,
          cursor,
          filterExperienceYearsMax,
          filterExperienceYearsMin,
          filterRating,
          filterMsaLocation,
          filterTagName,
          filterJobTitle,
          filterSearch,
        ),
      ...options,
    });
  };

  /**
   * Mutation `useBulkDelete`
   * @param { BizProfileModels.BulkDeleteBizProfileRequestDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.BulkDeleteBizProfileResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useBulkDelete = (
    options?: AppMutationOptions<
      typeof BizProfileApi.bulkDelete,
      { data: BizProfileModels.BulkDeleteBizProfileRequestDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizProfileApi.bulkDelete(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useBulkUpdate`
   * @param { BizProfileModels.BulkUpdateBizProfileRequestDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.BulkUpdateBizProfileResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useBulkUpdate = (
    options?: AppMutationOptions<
      typeof BizProfileApi.bulkUpdate,
      { data: BizProfileModels.BulkUpdateBizProfileRequestDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizProfileApi.bulkUpdate(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useListLocations`
   * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
   * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): location, city, state, count. Example: `location`
   * @param { number } object.page Query parameter. 1-indexed page number to begin from
   * @param { string } object.cursor Query parameter. ID of item to start after
   * @param { string } object.filterLocation Query parameter. Filter by location name (partial match). Example: `Los Angeles, CA`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ListLocationsResponse> }
   * @statusCodes [200]
   */
  export const useListLocations = <TData>(
    {
      limit,
      order,
      page,
      cursor,
      filterLocation,
    }: { limit: number; order?: string; page?: number; cursor?: string; filterLocation?: string },
    options?: AppQueryOptions<typeof BizProfileApi.listLocations, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.listLocations(limit, order, page, cursor, filterLocation),
      queryFn: () => BizProfileApi.listLocations(limit, order, page, cursor, filterLocation),
      ...options,
    });
  };

  /**
   * Query `useListJobTitles`
   * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
   * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): jobTitle, count. Example: `jobTitle`
   * @param { number } object.page Query parameter. 1-indexed page number to begin from
   * @param { string } object.cursor Query parameter. ID of item to start after
   * @param { BizProfileModels.ExperienceYearsFilterDto } object.filterExperienceYears Query parameter. Filter by experience years (range)
   * @param { string } object.filterMsaLocation Query parameter. Filter by MSA location name (exact match). Example: `Los Angeles-Long Beach-Anaheim, CA`
   * @param { string } object.filterJobTitle Query parameter. Filter by job title name (partial match). Example: `Software Developer`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ListJobTitlesResponse> }
   * @statusCodes [200]
   */
  export const useListJobTitles = <TData>(
    {
      limit,
      order,
      page,
      cursor,
      filterExperienceYears,
      filterMsaLocation,
      filterJobTitle,
    }: {
      limit: number;
      order?: string;
      page?: number;
      cursor?: string;
      filterExperienceYears?: BizProfileModels.ExperienceYearsFilterDto;
      filterMsaLocation?: string;
      filterJobTitle?: string;
    },
    options?: AppQueryOptions<typeof BizProfileApi.listJobTitles, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.listJobTitles(
        limit,
        order,
        page,
        cursor,
        filterExperienceYears,
        filterMsaLocation,
        filterJobTitle,
      ),
      queryFn: () =>
        BizProfileApi.listJobTitles(
          limit,
          order,
          page,
          cursor,
          filterExperienceYears,
          filterMsaLocation,
          filterJobTitle,
        ),
      ...options,
    });
  };

  /**
   * Query `useListJobPositions`
   * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
   * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): jobPosition, count. Example: `jobPosition`
   * @param { number } object.page Query parameter. 1-indexed page number to begin from
   * @param { string } object.cursor Query parameter. ID of item to start after
   * @param { string } object.filterJobPosition Query parameter. Filter by job position name (partial match). Example: `Software Developer`
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ListJobPositionsResponse> }
   * @statusCodes [200]
   */
  export const useListJobPositions = <TData>(
    {
      limit,
      order,
      page,
      cursor,
      filterJobPosition,
    }: { limit: number; order?: string; page?: number; cursor?: string; filterJobPosition?: string },
    options?: AppQueryOptions<typeof BizProfileApi.listJobPositions, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.listJobPositions(limit, order, page, cursor, filterJobPosition),
      queryFn: () => BizProfileApi.listJobPositions(limit, order, page, cursor, filterJobPosition),
      ...options,
    });
  };

  /**
   * Mutation `useMarkAllAsRead`
   * @param { BizProfileModels.BizProfileMarkAllAsReadRequestDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<void> }
   * @statusCodes [204]
   */
  export const useMarkAllAsRead = (
    options?: AppMutationOptions<
      typeof BizProfileApi.markAllAsRead,
      { data: BizProfileModels.BizProfileMarkAllAsReadRequestDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizProfileApi.markAllAsRead(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useGet`
   * @param { string } object.id Path parameter
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ShowBizProfileDto> } Biz profile
   * @statusCodes [200]
   */
  export const useGet = <TData>({ id }: { id: string }, options?: AppQueryOptions<typeof BizProfileApi.get, TData>) => {
    return useAuthGuardedQuery({
      queryKey: keys.get(id),
      queryFn: () => BizProfileApi.get(id),
      ...options,
    });
  };

  /**
   * Mutation `useUpdate`
   * @param { string } mutation.id Path parameter
   * @param { BizProfileModels.UpdateBizProfileRequestDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.ShowBizProfileDto> }
   * @statusCodes [200, 201]
   */
  export const useUpdate = (
    options?: AppMutationOptions<
      typeof BizProfileApi.update,
      { id: string; data: BizProfileModels.UpdateBizProfileRequestDto }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id, data }) => BizProfileApi.update(id, data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useDeleteBizProfileById`
   * @param { string } mutation.id Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.DeleteBizProfileResponseDto> } Biz profile deleted
   * @statusCodes [200]
   */
  export const useDeleteBizProfileById = (
    options?: AppMutationOptions<typeof BizProfileApi.deleteBizProfileById, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizProfileApi.deleteBizProfileById(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useFavorite`
   * @param { string } mutation.id Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.FavoriteBizProfileResponseDto> }
   * @statusCodes [200, 201]
   */
  export const useFavorite = (
    options?: AppMutationOptions<typeof BizProfileApi.favorite, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizProfileApi.favorite(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useUnfavorite`
   * @param { string } mutation.id Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.UnfavoriteBizProfileResponseDto> } Biz profile unfavorited
   * @statusCodes [200]
   */
  export const useUnfavorite = (
    options?: AppMutationOptions<typeof BizProfileApi.unfavorite, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizProfileApi.unfavorite(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useGetJobSkillMatrix`
   * @param { string } object.id Path parameter
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ProfileJobSkillMatrixResponseDto> } Job skill matrix
   * @statusCodes [200]
   */
  export const useGetJobSkillMatrix = <TData>(
    { id }: { id: string },
    options?: AppQueryOptions<typeof BizProfileApi.getJobSkillMatrix, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.getJobSkillMatrix(id),
      queryFn: () => BizProfileApi.getJobSkillMatrix(id),
      ...options,
    });
  };

  /**
   * Mutation `useRetryProcessingPipeline`
   * @param { string } mutation.id Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<BizProfileModels.RetryProcessingPipelineResponseDto> }
   * @statusCodes [201]
   */
  export const useRetryProcessingPipeline = (
    options?: AppMutationOptions<typeof BizProfileApi.retryProcessingPipeline, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizProfileApi.retryProcessingPipeline(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Query `useDownloadResume`
   * @param { string } object.id Path parameter
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizProfileModels.ProfileResumeDownloadResponseDto> } Provides the download URL for the resume file
   * @statusCodes [200]
   */
  export const useDownloadResume = <TData>(
    { id }: { id: string },
    options?: AppQueryOptions<typeof BizProfileApi.downloadResume, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.downloadResume(id),
      queryFn: () => BizProfileApi.downloadResume(id),
      ...options,
    });
  };

  /**
   * Mutation `useMarkAsRead`
   * @param { string } mutation.id Path parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<void> }
   * @statusCodes [204]
   */
  export const useMarkAsRead = (
    options?: AppMutationOptions<typeof BizProfileApi.markAsRead, { id: string }> & InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ id }) => BizProfileApi.markAsRead(id),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };
}
